import React, { useState, useRef, useEffect } from 'react';
import { phoneAuthService, PhoneAuthResult, PhoneVerificationResult } from '../services/phoneAuth';
import { authApi, TokenManager } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { User } from 'firebase/auth';
import TermsOfServiceModal from './TermsOfServiceModal';
import './PhoneLoginModal.css';

interface PhoneLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFirebaseSuccess: (user: User, isExistingUser?: boolean) => void;
  forceLogin?: boolean;
  showNameUpdateOnly?: boolean; // Chỉ hiển thị form cập nhật tên
}

type LoginStep = 'phone' | 'otp' | 'name';

const PhoneLoginModal: React.FC<PhoneLoginModalProps> = ({
  isOpen,
  onClose,
  onFirebaseSuccess,
  forceLogin = false,
  showNameUpdateOnly = false
}) => {
  const [step, setStep] = useState<LoginStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const { refreshUser } = useAuth();
  
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  // Vietnamese phone number regex
  const phoneRegex = /^(0[3|5|7|8|9])+([0-9]{8})$/;

  // Referral code validation (6 alphanumeric characters)
  const referralCodeRegex = /^[A-Z0-9]{6}$/i;

  // Normalize referral code to uppercase
  const normalizeReferralCode = (code: string): string => {
    return code.toUpperCase().trim();
  };

  // Timer for resend code
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Initialize reCAPTCHA when needed
  useEffect(() => {
    if (isOpen && step === 'phone') {
      phoneAuthService.initializeRecaptcha('recaptcha-container-login');
    }
    
    return () => {
      if (!isOpen) {
        phoneAuthService.cleanup();
      }
    };
  }, [isOpen, step]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (showNameUpdateOnly) {
        setStep('name');
      } else {
        setStep('phone');
      }
      setPhoneNumber('');
      setOtpCode(['', '', '', '', '', '']);
      setName('');
      setError('');
      setTimer(0);
      setFirebaseUser(null);
      setIsExistingUser(false);
      setTermsAccepted(false);
    }
  }, [isOpen, showNameUpdateOnly]);

  const validatePhoneNumber = (phone: string): boolean => {
    return phoneRegex.test(phone);
  };

  const formatPhoneForDisplay = (phone: string): string => {
    if (phone.startsWith('0')) {
      return `+84${phone.substring(1)}`;
    }
    return phone;
  };

  const handlePhoneSubmit = async () => {
    if (!phoneNumber.trim()) {
      setError('Vui lòng nhập số điện thoại');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError('Số điện thoại không đúng định dạng (0xxxxxxxxx - 10 chữ số)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result: PhoneAuthResult = await phoneAuthService.sendVerificationCode(phoneNumber);
      
      if (result.success) {
        setStep('otp');
        setTimer(60);
      } else {
        setError(result.error || 'Không thể gửi mã xác thực');
      }
    } catch (error) {
      setError('Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    if (value && !/^\d$/.test(value)) return; // Only allow digits

    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);

    // Auto focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) {
      setError('Vui lòng nhập đầy đủ mã OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result: PhoneVerificationResult = await phoneAuthService.verifyCode(code);
      
      if (result.success && result.user) {
        setFirebaseUser(result.user);
        
        // Lấy Firebase ID Token để gửi lên server
        const idToken = await result.user.getIdToken();
        
        // Kiểm tra user đã tồn tại trong hệ thống chưa bằng Firebase token
        try {
          const loginResponse = await authApi.webLogin({
            name: '', // Tạm thời để trống, server sẽ lấy phone từ Firebase token
            firebaseToken: idToken // Gửi token thay vì phone number
          });


          if (loginResponse.success && loginResponse.data) {
            const { isNewUser, tokens } = loginResponse.data;

            if (isNewUser) {
              // User mới, chuyển sang step nhập tên và mã giới thiệu
              setIsExistingUser(false);
              setStep('name');
            } else {
              // User đã tồn tại, login thành công
              setIsExistingUser(true);

              // Lưu tokens nếu có
              if (tokens) {
                TokenManager.setTokens(tokens.accessToken, tokens.refreshToken);

                // Refresh user data trong AuthContext
                await refreshUser();
              }

              onFirebaseSuccess(result.user, true);
              handleClose();
            }
          } else {
            console.error('Login response unsuccessful:', loginResponse);
            setError('Đăng nhập không thành công');
          }
        } catch (checkError: any) {
          console.error('WebLogin error:', checkError);
          // Kiểm tra xem có phải user mới hay không
          if (checkError.response?.status === 404) {
            const errorData = checkError.response?.data;
            if (errorData?.data?.isNewUser) {
              // User mới, chuyển sang step nhập tên
              setIsExistingUser(false);
              setStep('name');
            } else {
              setError('Đã xảy ra lỗi. Vui lòng thử lại.');
            }
          } else {
            setError('Đã xảy ra lỗi. Vui lòng thử lại.');
          }
        }
      } else {
        setError(result.error || 'Mã OTP không đúng');
      }
    } catch (error) {
      setError('Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async () => {
    if (!name.trim()) {
      setError('Vui lòng nhập tên của bạn');
      return;
    }

    if (!firebaseUser) {
      setError('Lỗi xác thực. Vui lòng thử lại.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Lấy Firebase ID Token để gửi lên server
      const idToken = await firebaseUser.getIdToken();
      
      if (isExistingUser || showNameUpdateOnly) {
        // User đã tồn tại -> Cập nhật tên
        const updateResponse = await authApi.updateName({
          name: name.trim(),
          firebaseToken: idToken
        });
        
        if (updateResponse.success) {
          // Refresh user data trong AuthContext sau khi update name
          console.log('Name updated, refreshing user data...');
          await refreshUser();
          
          onFirebaseSuccess(firebaseUser, true); // existing user
          handleClose();
        } else {
          setError(updateResponse.message || 'Không thể cập nhật tên');
        }
      } else {
        // User mới -> Tạo tài khoản
        const loginResponse = await authApi.webLogin({
          name: name.trim(),
          firebaseToken: idToken
        });
        
        if (loginResponse.success && loginResponse.data) {
          // Lưu tokens cho user mới
          const { tokens } = loginResponse.data;
          if (tokens) {
            console.log('Saving tokens for new user...');
            TokenManager.setTokens(tokens.accessToken, tokens.refreshToken);

            // Refresh user data trong AuthContext
            console.log('Refreshing user data for new user...');
            await refreshUser();
          }

          // Submit referral code if provided (optional, non-blocking)
          if (referralCode && referralCode.trim()) {
            try {
              const normalizedCode = normalizeReferralCode(referralCode);

              // Validate referral code format
              if (!referralCodeRegex.test(normalizedCode)) {
                console.warn('Invalid referral code format:', normalizedCode);
                // Don't block registration, just skip referral
              } else {
                const { userApiService } = await import('../services/userApi');
                const referralResponse = await userApiService.submitReferralCode(normalizedCode);

                if (referralResponse.success) {
                  console.log('Referral code applied successfully');
                  // Refresh user again to get updated refBy field
                  await refreshUser();
                } else {
                  console.warn('Failed to apply referral code:', referralResponse.message);
                  // Don't block user registration if referral fails
                }
              }
            } catch (error) {
              console.error('Error submitting referral code:', error);
              // Don't block user registration if referral fails
            }
          }

          onFirebaseSuccess(firebaseUser, false); // new user
          handleClose();
        } else {
          setError(loginResponse.message || 'Không thể tạo tài khoản');
        }
      }
    } catch (error) {
      setError('Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;
    
    setOtpCode(['', '', '', '', '', '']);
    setError('');
    await handlePhoneSubmit();
  };

  const handleBack = () => {
    if (step === 'otp') {
      setStep('phone');
      setOtpCode(['', '', '', '', '', '']);
      setError('');
      setTimer(0);
    } else if (step === 'name') {
      setStep('otp');
      setName('');
      setError('');
    }
  };

  const handleClose = () => {
    if (forceLogin) return; // Không cho phép đóng nếu bắt buộc login
    
    setStep('phone');
    setPhoneNumber('');
    setOtpCode(['', '', '', '', '', '']);
    setName('');
    setError('');
    setTimer(0);
    setFirebaseUser(null);
    setIsExistingUser(false);
    phoneAuthService.cleanup();
    onClose();
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTerms(false);
  };

  const handleTermsDecline = () => {
    setShowTerms(false);
    // Don't set termsAccepted to true, user needs to accept to proceed
  };

  if (!isOpen) return null;

  return (
    <div className="phone-login-overlay">
      <div className="phone-login-modal">
        <div className="phone-login-header">
          <h2>
            {showNameUpdateOnly ? 'Cập nhật thông tin' : (
              <>
                {step === 'phone' && 'Đăng nhập với số điện thoại'}
                {step === 'otp' && 'Nhập mã xác thực'}
                {step === 'name' && (isExistingUser ? 'Cập nhật thông tin cá nhân' : 'Nhập thông tin cá nhân')}
              </>
            )}
          </h2>
          {!forceLogin && (
            <button className="close-button" onClick={handleClose}>×</button>
          )}
        </div>

        <div className="phone-login-content">
          {step === 'phone' && (
            <>
              <p className="description">
                Nhập số điện thoại để nhận mã xác thực OTP
              </p>
              
              <div className="input-group">
                <label htmlFor="phone">Số điện thoại</label>
                <input
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="0901234567"
                  className="phone-input"
                  disabled={loading}
                  maxLength={10}
                />
                <small className="helper-text">
                  Định dạng: 0xxxxxxxxx (10 chữ số, bắt đầu bằng 03, 05, 07, 08, 09)
                </small>
              </div>

              {error && <div className="error-message">{error}</div>}

              {/* Terms of Service acceptance */}
              <div className="terms-checkbox-group">
                <label className="terms-checkbox">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="checkmark"></span>
                  <span className="terms-text">
                    <span className="terms-agree-text">Tôi đồng ý với </span>
                    <span 
                      className="terms-link-text"
                      onClick={() => setShowTerms(true)}
                      style={{ cursor: loading ? 'default' : 'pointer' }}
                    >
                      Điều khoản sử dụng
                    </span>
                    <span className="terms-agree-text"> tham gia trò chơi VŨ TRỤ KILOBA</span>
                  </span>
                </label>
                {!termsAccepted && (
                  <small className="terms-required-note">
                    * Bạn cần đồng ý với Điều khoản sử dụng tham gia trò chơi VŨ TRỤ KILOBA để tiếp tục
                  </small>
                )}
              </div>

              <div className="button-group">
                <button
                  className="primary-button"
                  onClick={handlePhoneSubmit}
                  disabled={loading || !phoneNumber.trim() || !termsAccepted}
                >
                  {loading ? 'Đang gửi...' : 'Lấy mã xác thực'}
                </button>
              </div>
            </>
          )}

          {step === 'otp' && (
            <>
              <p className="description">
                Mã xác thực đã được gửi đến số <strong>{formatPhoneForDisplay(phoneNumber)}</strong>
              </p>

              <div className="otp-group">
                <label>Mã xác thực OTP</label>
                <div className="otp-inputs">
                  {otpCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="text"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="otp-input"
                      disabled={loading}
                      maxLength={1}
                    />
                  ))}
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                className="primary-button"
                onClick={handleOtpSubmit}
                disabled={loading || otpCode.join('').length !== 6}
              >
                {loading ? 'Đang xác thực...' : 'Xác thực'}
              </button>

              <div className="resend-section">
                <button
                  className={`resend-button ${timer > 0 ? 'disabled' : ''}`}
                  onClick={handleResendOtp}
                  disabled={timer > 0 || loading}
                >
                  {timer > 0 ? `Gửi lại sau ${timer}s` : 'Gửi lại mã'}
                </button>
              </div>

              <button className="back-button" onClick={handleBack}>
                ← Quay lại nhập số điện thoại
              </button>
            </>
          )}

          {step === 'name' && (
            <>
              <p className="description">
                {showNameUpdateOnly ? (
                  'Cập nhật tên hiển thị của bạn'
                ) : isExistingUser ? (
                  `Số điện thoại ${formatPhoneForDisplay(phoneNumber)} đã có trong hệ thống.
                  Bạn có muốn cập nhật tên hiển thị không?`
                ) : (
                  `Số điện thoại ${formatPhoneForDisplay(phoneNumber)} chưa có trong hệ thống.
                  Vui lòng nhập tên để tạo tài khoản mới.`
                )}
              </p>

              <div className="input-group">
                <label htmlFor="name">Họ và tên</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nhập họ và tên của bạn"
                  className="name-input"
                  disabled={loading}
                />
              </div>

              {/* Referral Code Input - Only show for new users */}
              {!isExistingUser && !showNameUpdateOnly && (
                <div className="input-group referral-code-input">
                  <label htmlFor="referralCode">
                    Mã giới thiệu (không bắt buộc)
                  </label>
                  <input
                    id="referralCode"
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Nhập mã 6 ký tự"
                    className="referral-input"
                    disabled={loading}
                    maxLength={6}
                  />
                  <small className="referral-hint">
                    Mã gồm 6 ký tự (chữ cái và số). VD: ABC123, XYZ789
                  </small>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}

              <button
                className="primary-button"
                onClick={handleNameSubmit}
                disabled={loading || !name.trim()}
              >
                {loading ? (
                  showNameUpdateOnly || isExistingUser ? 'Đang cập nhật...' : 'Đang tạo tài khoản...'
                ) : (
                  showNameUpdateOnly || isExistingUser ? 'Xác nhận' : 'Tạo tài khoản'
                )}
              </button>

              {!showNameUpdateOnly && (
                <button className="back-button" onClick={handleBack}>
                  ← Quay lại nhập mã OTP
                </button>
              )}
            </>
          )}
        </div>

        {/* Hidden reCAPTCHA container */}
        <div id="recaptcha-container-login" ref={recaptchaRef} style={{ display: 'none' }}></div>
      </div>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </div>
  );
};

export default PhoneLoginModal;
